<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import type { Project, CalendarEvent, WeekPeriod, Task } from '../lib/types';
  import {
    getWeekDisplayRange,
    getWeekStart,
    getWeekEnd,
    isCurrentWeek,
    isFutureWeek,
    isPastWeek,
    eventsForDay,
    isMultiDayEvent,
    eventEndDate,
    eventTotalDays,
    MS_PER_DAY,
    parseCalendarDate,
    getLocalToday,
    formatCalendarDate,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import {
    completeTask,
    deleteTask as deleteTaskApi,
    markTaskWontDo,
    updateTask,
  } from '../lib/taskApi';
  import { fetchProjectTasks, getCachedTasks } from '../lib/stores/projectTasksCache';
  import { timelinePanelProjects, type TaskPreview } from '../lib/projectLanesUtils';
  import { setupMaxVisible, MAX_VISIBLE_PRESETS } from '../lib/useMaxVisible';
  import {
    draggedEvent,
    openEventModal,
    openEventModalForEdit,
    openEventsRailForDay,
    openProjectModalForCreate,
    requestEventMove,
  } from '../lib/stores/ui';
  import ProjectPreviewCard from './ProjectPreviewCard.svelte';
  import TaskDetailPopover from './TaskDetailPopover.svelte';
  import {
    showDayCellPopover,
    hideDayCellPopover,
    hoveredDateStr,
  } from '../lib/stores/dayCellPopover';
  import { setCompactDragImage } from '../lib/dragImage';
  import { cellClickMode } from '../lib/stores/cellClick';
  import { viewport } from '../lib/stores/viewport';

  type DaysScale = 'week' | 'month';

  interface Props {
    visibleWeeks: string[];
    events: CalendarEvent[];
    currentWeek: string;
    currentMonth?: string;
    period: WeekPeriod;
    daysScale?: DaysScale;
    /** All scheduled planned/active projects, independent of the visible date range. */
    allProjects?: Project[];
  }

  let {
    visibleWeeks,
    events,
    currentWeek,
    currentMonth = '',
    period = 'month',
    daysScale = 'week',
    allProjects = [],
  }: Props = $props();

  // Period options for the selector (same for both scales)
  const periodOptions: { value: string; label: string }[] = [
    { value: '1-week', label: '1 Week' },
    { value: 'month', label: 'Month' },
    { value: '5', label: '5 weeks' },
    { value: '7', label: '7 weeks' },
    { value: 'quarter', label: 'Quarter' },
  ];

  // Day names for the header
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Month names for first-of-month labels
  const monthAbbr = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // === Month Scale Helpers ===

  // Group weeks by the month they primarily belong to.
  // A week belongs to the month that contains its Thursday (ISO convention for "majority").
  interface MonthGroup {
    monthKey: string; // e.g. "2026-02"
    monthLabel: string; // e.g. "Feb"
    yearLabel: string; // e.g. "2026"
    weeks: string[];
  }

  let monthGroups = $derived.by((): MonthGroup[] => {
    if (daysScale !== 'month') return [];

    const groups: MonthGroup[] = [];
    let currentGroup: MonthGroup | null = null;

    for (const weekKey of visibleWeeks) {
      const weekStart = getWeekStart(weekKey);
      // Thursday of the week = weekStart (Sunday) + 4 days
      const thursday = new Date(weekStart);
      thursday.setUTCDate(weekStart.getUTCDate() + 4);
      const mk = thursday.toISOString().slice(0, 7);

      if (!currentGroup || currentGroup.monthKey !== mk) {
        const monthIndex = thursday.getUTCMonth();
        currentGroup = {
          monthKey: mk,
          monthLabel: monthAbbr[monthIndex],
          yearLabel: String(thursday.getUTCFullYear()),
          weeks: [],
        };
        groups.push(currentGroup);
      }
      currentGroup.weeks.push(weekKey);
    }

    return groups;
  });

  // Check if a day cell is the first of its month
  function isFirstOfMonth(weekKey: string, dayIndex: number): boolean {
    const date = getDayDate(weekKey, dayIndex);
    return date.getUTCDate() === 1;
  }

  // Get abbreviated month name for a day
  function getDayMonthAbbr(weekKey: string, dayIndex: number): string {
    const date = getDayDate(weekKey, dayIndex);
    return monthAbbr[date.getUTCMonth()];
  }

  function setPeriod(newPeriod: string) {
    if (newPeriod === period) return;

    const params = new URLSearchParams();
    params.set('view', 'days');
    // Default range is 'month' — only include in URL if different
    if (newPeriod !== 'month') {
      params.set('range', newPeriod);
    }

    if (daysScale === 'month') {
      router.get(`/month/${currentMonth}?${params.toString()}`, {}, { preserveScroll: true });
    } else {
      router.get(`/week/${currentWeek}?${params.toString()}`, {}, { preserveScroll: true });
    }
  }

  // === Projects panel (lanes-style) ===

  // Scheduled planned/active projects — active first, then planned (see projectLanesUtils).
  let panelProjects = $derived(timelinePanelProjects(allProjects));

  // Task detail popover state (same UX as the Projects Lanes view)
  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);
  let popoverProjectId = $state<string | null>(null);

  function tasksFor(projectId: string): Task[] {
    return getCachedTasks(projectId).tasks;
  }

  async function handleTaskClick(
    project: Project,
    preview: TaskPreview,
    event: MouseEvent | KeyboardEvent,
  ) {
    const anchor = event.currentTarget as HTMLElement;
    if (popoverTask?.id === preview.id) {
      closePopover();
      return;
    }

    let task = tasksFor(project.id).find((item) => item.id === preview.id) ?? null;
    if (!task) {
      await fetchProjectTasks(project.id);
      task = tasksFor(project.id).find((item) => item.id === preview.id) ?? null;
    }
    if (!task) return;

    popoverTask = task;
    popoverAnchorEl = anchor;
    popoverProjectId = project.id;
  }

  function closePopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    if (!popoverProjectId) return;
    const result = await updateTask(popoverProjectId, task.id, updates, tasksFor(popoverProjectId));
    if (result.success && popoverTask?.id === task.id) {
      popoverTask = result.tasks.find((item) => item.id === task.id) ?? { ...task, ...updates };
    }
  }

  async function handlePopoverDelete(task: Task, pendingUpdates?: Partial<Task>) {
    if (!popoverProjectId) return;
    if (pendingUpdates) await handlePopoverUpdate(task, pendingUpdates);
    closePopover();
    await deleteTaskApi(popoverProjectId, task.id, tasksFor(popoverProjectId));
  }

  async function handlePopoverWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    if (!popoverProjectId) return;
    closePopover();
    await markTaskWontDo(popoverProjectId, task.id, tasksFor(popoverProjectId), pendingUpdates);
  }

  async function handlePopoverToggle(task: Task, pendingUpdates?: Partial<Task>) {
    if (!popoverProjectId) return;
    closePopover();
    await completeTask(popoverProjectId, task.id, tasksFor(popoverProjectId), pendingUpdates);
  }

  // Get events for a specific day (single-day only; multi-day use spanning bars)
  function getDayEvents(weekKey: string, dayIndex: number): CalendarEvent[] {
    const weekStart = getWeekStart(weekKey);
    const targetDate = new Date(weekStart);
    targetDate.setUTCDate(weekStart.getUTCDate() + dayIndex);
    const targetDateStr = targetDate.toISOString().split('T')[0];

    return sortEventsForDisplay(
      eventsForDay(events, targetDateStr).filter((e) => !isMultiDayEvent(e)),
    );
  }

  // Spanning events for multi-day events across a week row
  interface SpanningEvent {
    event: CalendarEvent;
    startCol: number; // 0-6
    colSpan: number;
    row: number; // 0-based lane assignment
    continuesLeft: boolean;
    continuesRight: boolean;
  }

  const SPAN_ROW_HEIGHT = 18; // px per spanning event row
  const DAY_NUMBER_HEIGHT = 14; // px reserved for the day number at top of each cell

  function getSpanningEvents(weekKey: string): SpanningEvent[] {
    const weekStart = getWeekStart(weekKey);
    const weekEnd = getWeekEnd(weekKey);
    const weekStartStr = weekStart.toISOString().slice(0, 10);
    const weekEndStr = weekEnd.toISOString().slice(0, 10);

    const spans = events
      .filter((e) => isMultiDayEvent(e) && e.date <= weekEndStr && eventEndDate(e) >= weekStartStr)
      .map((e) => {
        const eStart = parseCalendarDate(e.date);
        const eEnd = parseCalendarDate(eventEndDate(e));

        const startCol = Math.max(
          0,
          Math.round((eStart.getTime() - weekStart.getTime()) / MS_PER_DAY),
        );
        const endCol = Math.min(6, Math.round((eEnd.getTime() - weekStart.getTime()) / MS_PER_DAY));

        return {
          event: e,
          startCol,
          colSpan: endCol - startCol + 1,
          row: 0,
          continuesLeft: eStart < weekStart,
          continuesRight: eEnd > weekEnd,
        };
      })
      // Sort by startCol then by longest span first (for better lane packing)
      .sort((a, b) => a.startCol - b.startCol || b.colSpan - a.colSpan);

    // Lane assignment: greedily assign each span to the lowest row that doesn't overlap
    // lanes[row] = rightmost occupied column (exclusive) in that row
    const lanes: number[] = [];
    for (const span of spans) {
      let assigned = false;
      for (let r = 0; r < lanes.length; r++) {
        if (lanes[r] <= span.startCol) {
          span.row = r;
          lanes[r] = span.startCol + span.colSpan;
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        span.row = lanes.length;
        lanes.push(span.startCol + span.colSpan);
      }
    }

    return spans;
  }

  /** How many rows of spanning events a week has (for padding) */
  function spanRowCount(spanningEvents: SpanningEvent[]): number {
    if (spanningEvents.length === 0) return 0;
    return Math.max(...spanningEvents.map((s) => s.row)) + 1;
  }

  // Get the date for a specific day in a week
  function getDayDate(weekKey: string, dayIndex: number): Date {
    const weekStart = getWeekStart(weekKey);
    const date = new Date(weekStart);
    date.setUTCDate(weekStart.getUTCDate() + dayIndex);
    return date;
  }

  // Format date as day number
  function getDayNumber(weekKey: string, dayIndex: number): string {
    const date = getDayDate(weekKey, dayIndex);
    return String(date.getUTCDate());
  }

  // Check if a day is today (compare UTC calendar date from week with local calendar date)
  function isToday(weekKey: string, dayIndex: number): boolean {
    const date = getDayDate(weekKey, dayIndex);
    const today = getLocalToday();
    return (
      date.getUTCFullYear() === today.getUTCFullYear() &&
      date.getUTCMonth() === today.getUTCMonth() &&
      date.getUTCDate() === today.getUTCDate()
    );
  }

  // Determine week state
  function getWeekState(weekKey: string): 'past' | 'current' | 'future' {
    if (isCurrentWeek(weekKey)) return 'current';
    if (isPastWeek(weekKey)) return 'past';
    return 'future';
  }

  function navigateToWeek(weekKey: string) {
    router.get(`/week/${weekKey}?view=week-focus`, {}, { preserveScroll: true });
  }

  // Dynamic max items for the events day columns
  let weekGrid: HTMLElement;
  let maxVisibleEvents = $state<number>(MAX_VISIBLE_PRESETS.eventDot.defaultVisible);

  onMount(() => {
    const cleanups: (() => void)[] = [];

    // Setup observer for events day column
    const dayEvents = weekGrid?.querySelector('.day-events') as HTMLElement;
    if (dayEvents) {
      cleanups.push(
        setupMaxVisible(dayEvents, MAX_VISIBLE_PRESETS.eventDot, (max) => {
          maxVisibleEvents = max;
        }),
      );
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  });

  // Drag and drop for events
  let eventDropTarget = $state<{ weekKey: string; dayIndex: number } | null>(null);
  let draggingEventId = $state<string | null>(null);

  function handleEventDragStart(e: DragEvent, event: CalendarEvent) {
    draggingEventId = event.id;
    draggedEvent.set(event.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/event', String(event.id));
      setCompactDragImage(e, event.title, event.type, isMultiDayEvent(event));
    }
  }

  function handleEventDragEnd() {
    draggingEventId = null;
    draggedEvent.set(null);
  }

  function handleEventDragOver(e: DragEvent, weekKey: string, dayIndex: number) {
    e.preventDefault();
    if ($draggedEvent) {
      eventDropTarget = { weekKey, dayIndex };
    }
  }

  function handleEventDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      eventDropTarget = null;
    }
  }

  function handleEventDrop(e: DragEvent, weekKey: string, dayIndex: number) {
    e.preventDefault();
    eventDropTarget = null;

    // Only process if an event (not a project) is being dragged
    if (!$draggedEvent) return;

    const eventId =
      e.dataTransfer?.getData('application/event') || e.dataTransfer?.getData('text/plain');
    if (eventId) {
      const date = getDayDate(weekKey, dayIndex);
      const dateStr = date.toISOString().split('T')[0];
      const eventObj = events.find((candidate) => candidate.id === eventId);
      if (eventObj) {
        requestEventMove(eventId, eventObj, 'day', { date: dateStr });
      } else {
        router.post(
          `/calendar_events/${eventId}/move`,
          { date: dateStr },
          { preserveScroll: true },
        );
      }
    }
  }

  // Add a project from the panel header (context = current week/month)
  function handleAddProject() {
    if (daysScale === 'month' && currentMonth) {
      openProjectModalForCreate({
        schedulingState: 'scheduled',
        monthKey: currentMonth,
        targetDate: `${currentMonth}-01`,
        status: 'planned',
      });
      return;
    }
    const weekStart = getWeekStart(currentWeek);
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: weekStart.toISOString().slice(0, 7),
      targetDate: weekStart.toISOString().split('T')[0],
      status: 'planned',
    });
  }

  function handleAddEvent(weekKey: string, dayIndex: number) {
    const date = getDayDate(weekKey, dayIndex);
    const dateStr = date.toISOString().split('T')[0];
    openEventModal(dateStr);
  }

  function handleEventClick(event: CalendarEvent) {
    openEventModalForEdit(event);
  }

  function handleViewMoreEvents(weekKey: string, dayIndex: number) {
    const date = getDayDate(weekKey, dayIndex);
    const dateStr = date.toISOString().split('T')[0];
    const label = formatCalendarDate(date, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    openEventsRailForDay(dateStr, label);
  }

  // Get event type color class
  function getEventDotColor(type: string): string {
    switch (type) {
      case 'appointment':
        return 'bg-blue-400';
      case 'travel':
        return 'bg-purple-400';
      case 'event':
        return 'bg-green-400';
      case 'deadline':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  }

  // Popover hover handlers for day cells (matching events_calendar behavior)
  function handleDayCellEnter(e: MouseEvent, weekKey: string, dayIndex: number) {
    const el = e.currentTarget as HTMLElement;
    const date = getDayDate(weekKey, dayIndex);
    const dateStr = date.toISOString().split('T')[0];
    const dayNames_full = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const label = `${dayNames_full[date.getUTCDay()]} ${monthNames[date.getUTCMonth()]} ${date.getUTCDate()}`;
    const allDayEvents = eventsForDay(events, dateStr);
    showDayCellPopover(dateStr, label, allDayEvents, el);
  }

  function handleDayCellLeave() {
    hideDayCellPopover();
  }

  function handleDayClick(e: MouseEvent, weekKey: string, dayIndex: number) {
    const target = e.target as HTMLElement;
    // Only trigger if clicking on the container background, not on items
    if (
      target.closest('.event-dot') ||
      target.closest('.more-count') ||
      target.closest('.day-add-btn')
    ) {
      return;
    }
    hideDayCellPopover(true);
    handleAddEvent(weekKey, dayIndex);
  }
</script>

<!-- Snippet: renders the 7-day events grid for a single week row -->
{#snippet eventsWeekRow(weekKey: string)}
  {@const spanningEvents = getSpanningEvents(weekKey)}
  {@const spanRows = spanRowCount(spanningEvents)}
  {@const spanPadding = spanRows * SPAN_ROW_HEIGHT}
  <div class="events-col">
    <div class="days-grid">
      {#if spanningEvents.length > 0}
        <div class="spanning-layer" style="top: {DAY_NUMBER_HEIGHT}px; height: {spanPadding}px;">
          {#each spanningEvents as span (span.event.id)}
            <button
              type="button"
              class="spanning-bar event-{span.event.type}"
              class:continues-left={span.continuesLeft}
              class:continues-right={span.continuesRight}
              class:dragging={draggingEventId === span.event.id}
              draggable={!$viewport.isMobile}
              ondragstart={(e) => handleEventDragStart(e, span.event)}
              ondragend={handleEventDragEnd}
              onclick={() => handleEventClick(span.event)}
              style="
                top: {span.row * SPAN_ROW_HEIGHT}px;
                left: calc({span.startCol} / 7 * 100%);
                width: calc({span.colSpan} / 7 * 100%);
              "
              title="{span.event.title} ({eventTotalDays(span.event)} days)"
            >
              <span class="spanning-title">{span.event.title}</span>
              <span class="spanning-days">{eventTotalDays(span.event)}d</span>
            </button>
          {/each}
        </div>
      {/if}
      {#each Array(7) as _, dayIndex}
        {@const dayEvents = getDayEvents(weekKey, dayIndex)}
        {@const visibleEvents = dayEvents.slice(0, maxVisibleEvents)}
        {@const hiddenCount = Math.max(0, dayEvents.length - maxVisibleEvents)}
        {@const dayIsToday = isToday(weekKey, dayIndex)}
        {@const isDropTarget =
          eventDropTarget?.weekKey === weekKey && eventDropTarget?.dayIndex === dayIndex}
        {@const dayDateStr = getDayDate(weekKey, dayIndex).toISOString().split('T')[0]}
        {@const isHovered = $hoveredDateStr === dayDateStr}
        {@const isMonthFirst = isFirstOfMonth(weekKey, dayIndex)}

        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div
          class="day-column"
          class:today={dayIsToday}
          class:drop-target={isDropTarget}
          class:has-events={dayEvents.length > 0}
          class:hovered-cell={isHovered}
          class:first-of-month={isMonthFirst}
          role="group"
          onmouseenter={(e) => handleDayCellEnter(e, weekKey, dayIndex)}
          onmouseleave={handleDayCellLeave}
          ondragover={(e) => handleEventDragOver(e, weekKey, dayIndex)}
          ondragleave={handleEventDragLeave}
          ondrop={(e) => handleEventDrop(e, weekKey, dayIndex)}
          onclick={(e) => {
            if ($cellClickMode === 'single') handleDayClick(e, weekKey, dayIndex);
          }}
          ondblclick={(e) => {
            if ($cellClickMode === 'double') handleDayClick(e, weekKey, dayIndex);
          }}
        >
          <span class="day-number" class:today={dayIsToday}>
            {#if isMonthFirst}
              <span class="month-marker">{getDayMonthAbbr(weekKey, dayIndex)}</span>
            {/if}
            {getDayNumber(weekKey, dayIndex)}
          </span>
          {#if spanRows > 0}
            <div class="span-spacer" style="height: {spanPadding}px;"></div>
          {/if}
          <div class="day-events">
            {#each visibleEvents as event (event.id)}
              {@const timeStr = formatEventTimeShort(event)}
              <button
                type="button"
                class="event-dot"
                class:dragging={draggingEventId === event.id}
                draggable={!$viewport.isMobile}
                ondragstart={(e) => handleEventDragStart(e, event)}
                ondragend={handleEventDragEnd}
                onclick={() => handleEventClick(event)}
                title={timeStr ? `${timeStr} ${event.title}` : event.title}
              >
                <span class="dot {getEventDotColor(event.type)}"></span>
                {#if timeStr}<span class="event-time">{timeStr}</span>{/if}
                <span class="event-title">{event.title}</span>
              </button>
            {/each}
            {#if hiddenCount > 0}
              <button
                type="button"
                class="more-count"
                onclick={() => handleViewMoreEvents(weekKey, dayIndex)}
                title="View all events"
              >
                +{hiddenCount}
              </button>
            {/if}
          </div>
          <button
            type="button"
            class="day-add-btn hover-reveal"
            onclick={() => handleAddEvent(weekKey, dayIndex)}
            title="Add event"
          >
            +
          </button>
        </div>
      {/each}
    </div>
  </div>
{/snippet}

<div class="table-container" class:month-scale={daysScale === 'month'}>
  <!-- Header row: [Week/Month | Events | Projects] -->
  <div class="table-header">
    <div class="header-cell week-col">
      {#if daysScale === 'month'}Month{:else}Week{/if}
    </div>
    <div class="header-cell events-col">
      <div class="events-header-row">
        <span class="events-label">📅 Events</span>
        <!-- On phones the range lives in the Timeline's View options sheet. -->
        {#if !$viewport.isMobile}
          <div class="header-controls">
            <div class="period-selector">
              {#each periodOptions as opt}
                <button
                  type="button"
                  class="period-btn"
                  class:active={period === opt.value}
                  onclick={() => setPeriod(opt.value)}
                >
                  {opt.label}
                </button>
              {/each}
            </div>
          </div>
        {/if}
      </div>
      <div class="day-headers">
        {#each dayNames as day}
          <span class="day-header">{day}</span>
        {/each}
      </div>
    </div>
    <div class="header-cell projects-col">
      <span class="projects-label">📋 Projects</span>
      <span class="project-count">{panelProjects.length}</span>
      <button type="button" class="panel-add-btn" onclick={handleAddProject} title="Add project">
        +
      </button>
    </div>
  </div>

  <div class="body">
    <!-- Left: week label + events rows (scrolls independently) -->
    <div class="week-grid" bind:this={weekGrid}>
      {#if daysScale === 'month'}
        <!-- MONTH SCALE: month label groups, events still per-week -->
        {#each monthGroups as group (group.monthKey)}
          <!-- flex-grow proportional to week count: every week row across groups gets an
               equal share of the container height (mirrors week-scale flex: 1 rows) -->
          <div class="month-group" style="flex-grow: {group.weeks.length};">
            <div class="month-col month-label-cell" style="grid-row: span {group.weeks.length};">
              <span class="month-name">{group.monthLabel}</span>
              <span class="month-year">{group.yearLabel}</span>
            </div>

            {#each group.weeks as weekKey (weekKey)}
              {@const state = getWeekState(weekKey)}
              <div
                class="week-row month-week-row"
                class:past={state === 'past'}
                class:current={state === 'current'}
                class:future={state === 'future'}
              >
                {@render eventsWeekRow(weekKey)}
              </div>
            {/each}
          </div>
        {/each}
      {:else}
        <!-- WEEK SCALE -->
        {#each visibleWeeks as weekKey (weekKey)}
          {@const state = getWeekState(weekKey)}
          <div
            class="week-row"
            class:past={state === 'past'}
            class:current={state === 'current'}
            class:future={state === 'future'}
          >
            <!-- Week label -->
            <div class="week-col week-label">
              <button type="button" class="week-link" onclick={() => navigateToWeek(weekKey)}>
                <span class="week-range">{getWeekDisplayRange(weekKey)}</span>
                <span class="week-number">{weekKey.split('-W')[1]}</span>
              </button>
            </div>

            <!-- Events column with 7 day mini-columns -->
            {@render eventsWeekRow(weekKey)}
          </div>
        {/each}
      {/if}
    </div>

    <!-- Right: projects panel (lanes-style card grid, independent of the date range) -->
    <div class="projects-panel">
      <div class="projects-grid">
        {#each panelProjects as project (project.id)}
          <ProjectPreviewCard
            {project}
            onTaskClick={(preview, event) => handleTaskClick(project, preview, event)}
          />
        {/each}
        {#if panelProjects.length === 0}
          <div class="projects-empty">No active or planned projects</div>
        {/if}
      </div>
    </div>
  </div>
</div>

<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  project={allProjects.find((p) => p.id === popoverProjectId) ?? null}
  onClose={closePopover}
  onUpdate={handlePopoverUpdate}
  onDelete={handlePopoverDelete}
  onMarkWontDo={handlePopoverWontDo}
  onToggleComplete={handlePopoverToggle}
/>

<style>
  .table-container {
    /* Projects panel width shared by header + body so they stay aligned */
    --projects-panel-width: clamp(16rem, 28vw, 30rem);
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    background: #f9fafb;
  }

  /* Header row */
  .table-header {
    display: flex;
    align-items: stretch;
    background: white;
    border-radius: 0.375rem 0.375rem 0 0;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .header-cell {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .header-cell.week-col {
    width: 5.5rem;
    flex-shrink: 0;
    justify-content: center;
    padding: 0.375rem 0.25rem;
    border-right: 1px solid #e5e7eb;
  }

  .table-container.month-scale .header-cell.week-col {
    width: 3.5rem;
  }

  .header-cell.events-col {
    flex: 1;
    min-width: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
    padding: 0.25rem 0.125rem;
  }

  .header-cell.projects-col {
    width: var(--projects-panel-width);
    flex-shrink: 0;
    border-left: 1px solid #e5e7eb;
    padding: 0.375rem 0.375rem;
  }

  .projects-label {
    flex-shrink: 0;
  }

  .project-count {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 0.1875rem;
    padding: 0.0625rem 0.3125rem;
    min-width: 1rem;
    text-align: center;
    flex-shrink: 0;
  }

  .panel-add-btn {
    margin-left: auto;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .panel-add-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  .events-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.25rem;
  }

  .events-label {
    flex-shrink: 0;
  }

  /* Header controls wrapper */
  .header-controls {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .day-headers {
    display: flex;
    flex: 1;
    gap: 1px;
    background: #e5e7eb;
    border-radius: 0.125rem;
    overflow: hidden;
  }

  .day-header {
    flex: 1;
    text-align: center;
    font-size: 0.5rem;
    font-weight: 600;
    color: #6b7280;
    padding: 0.125rem 0;
    background: #f9fafb;
  }

  /* Period selector */
  .period-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
    align-self: flex-end;
  }

  .period-btn {
    padding: 0.125rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
    text-transform: none;
    letter-spacing: 0;
  }

  .period-btn:hover {
    color: #374151;
    background: white;
  }

  .period-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Column widths */
  .week-col {
    width: 5.5rem;
    flex-shrink: 0;
    border-right: 1px solid #e5e7eb;
  }

  .events-col {
    flex: 1;
    min-width: 0;
  }

  /* Body: events area on the left, projects panel on the right */
  .body {
    flex: 1;
    display: flex;
    align-items: stretch;
    min-height: 0;
    background: white;
    border-radius: 0 0 0.375rem 0.375rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .week-grid {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .week-row {
    flex: 1;
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid #f3f4f6;
    /* Floor keeps rows usable when flex distributes space; low enough that the
       longest range (quarter ≈ 13 weeks) still fits a typical viewport without
       scrolling — event lists clip gracefully via maxVisibleEvents instead. */
    min-height: 3.5rem;
    transition: all 0.15s;
  }

  .week-row:last-child {
    border-bottom: none;
  }

  .week-row.past {
    background: #fafafa;
  }

  .week-row.current {
    background: #eff6ff;
  }

  .week-row.future {
    background: #fefefe;
  }

  /* Week label column */
  .week-label {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.02);
  }

  .week-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.0625rem;
    padding: 0.25rem 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 0.25rem;
    transition: background 0.15s;
  }

  .week-link:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  .week-range {
    font-size: 0.625rem;
    font-weight: 600;
    color: #374151;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }

  .week-number {
    font-size: 0.5rem;
    font-weight: 500;
    color: #9ca3af;
  }

  /* Projects panel (right side) */
  .projects-panel {
    width: var(--projects-panel-width);
    flex-shrink: 0;
    border-left: 1px solid #e5e7eb;
    overflow-y: auto;
    padding: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .projects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
    gap: 0.375rem;
    align-content: start;
  }

  .projects-empty {
    grid-column: 1 / -1;
    font-size: 0.6875rem;
    color: #94a3b8;
    text-align: center;
    padding: 1.5rem 0.5rem;
    border: 1.5px dashed #e2e8f0;
    border-radius: 0.375rem;
    background: #fafbfc;
  }

  /* Events column - 7 day mini-columns */
  .week-row .events-col {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 0.125rem;
    overflow: hidden;
  }

  .days-grid {
    display: flex;
    flex: 1;
    gap: 1px;
    background: #e5e7eb;
    border-radius: 0.125rem;
    overflow: hidden;
    position: relative;
  }

  .day-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: white;
    padding: 0.125rem;
    position: relative;
    transition: background 0.15s;
  }

  .day-column.today {
    background: white;
    box-shadow: inset 0 0 0 1.5px #3b82f6;
    z-index: 1;
  }

  .day-column.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }

  .day-column.hovered-cell {
    background: white;
    box-shadow: inset 0 0 0 1.5px #f59e0b;
    z-index: 1;
  }

  .day-column.hovered-cell.today {
    background: white;
    box-shadow: inset 0 0 0 1.5px #f59e0b;
  }

  .day-number {
    font-size: 0.5rem;
    font-weight: 500;
    color: #9ca3af;
    text-align: left;
    margin-bottom: 0.125rem;
    line-height: 1;
  }

  .day-number.today {
    color: #1d4ed8;
    font-weight: 700;
  }

  .day-events {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    flex: 1;
    overflow: hidden;
  }

  /* Event dot/pill - very compact */
  .event-dot {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.0625rem 0.125rem;
    background: transparent;
    border: none;
    cursor: grab;
    text-align: left;
    min-width: 0;
    overflow: hidden;
    border-radius: 0.125rem;
    transition: background 0.1s;
  }

  .event-dot:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  .event-dot.dragging {
    opacity: 0.5;
  }

  .dot {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .event-time {
    font-size: 0.5rem;
    color: #6b7280;
    font-weight: 600;
    flex-shrink: 0;
  }

  .event-title {
    font-size: 0.5rem;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .more-count {
    font-size: 0.4375rem;
    font-weight: 500;
    color: #6b7280;
    text-align: center;
    padding: 0.125rem 0.25rem;
    background: transparent;
    border: 1px dashed #9ca3af;
    border-radius: 0.125rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .more-count:hover {
    color: #3b82f6;
    border-color: #3b82f6;
    background: #eff6ff;
  }

  /* Day add button - top right on hover */
  .day-add-btn {
    position: absolute;
    top: 0;
    right: 0;
    width: 0.875rem;
    height: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    font-weight: 600;
    color: #9ca3af;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.125rem;
    cursor: pointer;
    transition: all 0.15s;
    opacity: 0;
    z-index: 1;
  }

  .day-column:hover .day-add-btn {
    opacity: 1;
  }

  .day-add-btn:hover {
    color: #3b82f6;
    border-color: #3b82f6;
    background: #eff6ff;
  }

  /* Spanning multi-day event bars — overlaid inside days-grid, below day numbers */
  .spanning-layer {
    position: absolute;
    /* top is set via inline style: DAY_NUMBER_HEIGHT px */
    left: 0;
    right: 0;
    z-index: 2;
    pointer-events: none; /* let clicks pass through gaps */
  }

  .span-spacer {
    flex-shrink: 0;
  }

  .spanning-bar {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    height: 16px;
    padding: 0 4px;
    border-radius: 3px;
    font-size: 0.5625rem;
    cursor: grab;
    border: none;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    pointer-events: auto; /* re-enable clicks on the bar itself */
    transition: opacity 0.15s;
    box-sizing: border-box;
  }

  .spanning-bar:hover {
    opacity: 0.85;
  }

  .spanning-bar.dragging {
    opacity: 0.4;
    cursor: grabbing;
  }

  .spanning-bar.continues-left {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    padding-left: 2px;
  }

  .spanning-bar.continues-right {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-right: 2px;
  }

  .spanning-bar.event-appointment {
    background: #dbeafe;
    color: #1e40af;
  }
  .spanning-bar.event-travel {
    background: #ede9fe;
    color: #5b21b6;
  }
  .spanning-bar.event-event {
    background: #dcfce7;
    color: #166534;
  }
  .spanning-bar.event-deadline {
    background: #fee2e2;
    color: #991b1b;
  }

  .spanning-title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .spanning-days {
    flex-shrink: 0;
    font-size: 0.5rem;
    opacity: 0.7;
  }

  /* Tailwind-like utility classes for event dots */
  .bg-blue-400 {
    background-color: #60a5fa;
  }
  .bg-purple-400 {
    background-color: #a78bfa;
  }
  .bg-green-400 {
    background-color: #4ade80;
  }
  .bg-red-400 {
    background-color: #f87171;
  }
  .bg-gray-400 {
    background-color: #9ca3af;
  }

  /* === Month Scale Layout === */

  .month-group {
    display: grid;
    /* month-col | events-col (per week row) */
    grid-template-columns: 3.5rem minmax(0, 1fr);
    /* Floor matches .week-row (3.5rem) so a quarter (~13 weeks) fits without scrolling. */
    grid-auto-rows: minmax(3.5rem, 1fr);
    border-bottom: 2px solid #e5e7eb;
    /* flex-grow is set inline (proportional to the group's week count) so groups
       stretch to fill the week-grid column; no min-height override, so groups keep
       their rows' floor and the container only scrolls in extreme cases. */
  }

  .month-group:last-child {
    border-bottom: none;
  }

  .month-col {
    width: 3.5rem;
    flex-shrink: 0;
    border-right: 1px solid #e5e7eb;
  }

  .month-label-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background: rgba(0, 0, 0, 0.02);
    /* grid-row: span N set via inline style */
    grid-column: 1;
  }

  .month-name {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
    letter-spacing: 0.01em;
  }

  .month-year {
    font-size: 0.5rem;
    font-weight: 500;
    color: #9ca3af;
  }

  /* Month-scale week rows: only the events-col */
  .month-week-row {
    grid-column: 2;
    display: flex;
    align-items: stretch;
    min-height: 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .month-week-row:last-child {
    border-bottom: none;
  }

  .month-week-row .events-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 0.125rem;
    overflow: hidden;
  }

  /* === First-of-month styling (month scale) === */

  .day-column.first-of-month {
    background: #fefce8;
  }

  .day-column.first-of-month.today {
    background: #eff6ff;
    box-shadow: inset 0 0 0 1.5px #3b82f6;
  }

  .day-column.first-of-month.hovered-cell {
    background: #fef9e0;
    box-shadow: inset 0 0 0 1.5px #f59e0b;
  }

  .month-marker {
    font-size: 0.4375rem;
    font-weight: 800;
    color: #b45309;
    letter-spacing: 0.02em;
    margin-right: 0.125rem;
  }
</style>
